import React from "react";
import { Link, useNavigate } from "react-router-dom";

import { ERROR_CODE } from "~/api/api.types";
import { Button, Title } from "~/components";
import { ROUTES } from "~/router";
import AuthBanner, { BannerTypes } from "~/screens/Auth/components/AuthBanner";
import { useTokenLogin } from "~/screens/Auth/hooks/useTokenLogin";
import { AuthLayout } from "~/screens/Auth/Layout/AuthLayout";
import { usePageTitle } from "~/shared";
import HighlightedText from "~/ui/common/HighlightedText";

export const MagicLink = () => {
  usePageTitle("Magic link");
  const navigate = useNavigate();

  const handleSuccess = () => {
    navigate(ROUTES.hubHome, { replace: true });
  };

  const { isLoggingIn, loginError, loginErrorCode, email } = useTokenLogin({
    onSuccess: handleSuccess,
  });

  if (isLoggingIn) {
    return (
      <AuthLayout banner={<AuthBanner type={BannerTypes.accountVerified} />}>
        <section className="flex w-full flex-col items-center justify-center space-y-4">
          <section className="w-full max-w-lg space-y-6">
            <Title>
              Logging you <HighlightedText>in</HighlightedText>...
            </Title>
            <p className="text-indigo mb-3 text-justify">
              Please wait a moment while we log you in automatically.
            </p>
          </section>
        </section>
      </AuthLayout>
    );
  }

  if (loginError && loginErrorCode === ERROR_CODE.magicLinkExpired) {
    return (
      <AuthLayout banner={<AuthBanner type={BannerTypes.codeValidation} />}>
        <section className="flex w-full flex-col items-center justify-center space-y-4">
          <section className="w-full max-w-lg space-y-6">
            <Title>
              This link has <HighlightedText>expired</HighlightedText>
            </Title>
            <p className="text-indigo mb-3 text-justify">
              {email
                ? `The magic link sent to ${email} has expired.`
                : "Your magic link has expired."}{" "}
              Please ask your admin staff to send you a new one.
            </p>

            <Button asChild size="default" className="w-full md:w-auto">
              <Link to={ROUTES.fullLogin}>Go to log in</Link>
            </Button>
          </section>
        </section>
      </AuthLayout>
    );
  }

  if (loginError) {
    return (
      <AuthLayout banner={<AuthBanner type={BannerTypes.codeValidation} />}>
        <section className="flex w-full flex-col items-center justify-center space-y-4">
          <section className="w-full max-w-lg space-y-6">
            <Title>
              Something went <HighlightedText>wrong</HighlightedText>
            </Title>
            <p className="text-indigo mb-3 text-justify">
              We were unable to log you in automatically. Please try again or
              contact support.
            </p>

            <Button asChild size="default" className="w-full md:w-auto">
              <Link to={ROUTES.fullLogin}>Log in</Link>
            </Button>
          </section>
        </section>
      </AuthLayout>
    );
  }

  // Fallback: no params at all
  return (
    <AuthLayout
      banner={<AuthBanner type={BannerTypes.accountVerified} />}
      showSupport
    >
      <section className="flex w-full flex-col items-center justify-center space-y-4">
        <section className="w-full max-w-lg space-y-6">
          <Title>
            Your <HighlightedText>magic link</HighlightedText> is here!
          </Title>
          <p className="text-indigo mb-3 text-justify">
            Click the button below to log in to your account.
          </p>

          <Button asChild size="default" className="w-full md:w-auto">
            <Link to={ROUTES.fullLogin}>Log in</Link>
          </Button>
        </section>
      </section>
    </AuthLayout>
  );
};
